import { useState } from "react";
import { useCapabilities, useWorkspaceServices } from "../context";
import { DEFAULT_SEGMENT_NOUN, type SegmentNoun } from "../vocabulary";

/**
 * KEY-592: the one-off "do you want notifications from this dial?" question, asked the first time somebody opens
 * a dial. People kept forgetting to press Subscribe and then heard nothing, but subscribing everyone to everything
 * by default is its own noise problem — so it is asked, once, and either answer puts it away for good (server-side,
 * per person, so a shared tablet does not answer it for the next pair of hands).
 *
 * The server decides whether to offer it (`detail.offerFollowPrompt`): never to someone already following the dial,
 * here or through a group above it. On a group the answer covers every dial in it, which is what the copy says.
 */
export function FollowPrompt({
  instanceId,
  dialName,
  isGroup,
  onFollowed,
  noun = DEFAULT_SEGMENT_NOUN,
}: {
  instanceId: string;
  dialName: string;
  isGroup: boolean;
  /** What this dial calls an action point (KEY-539/KEY-721); the platform's word when omitted. */
  noun?: SegmentNoun;
  /** Called after a follow lands, so the Subscribe controls pick it up. */
  onFollowed: () => void | Promise<void>;
}) {
  const { api, getAccessToken } = useWorkspaceServices();
  // Nothing to follow in a preview: the host's subscriptions capability is off there.
  const canFollow = useCapabilities().subscriptions;
  const [answered, setAnswered] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (answered || !canFollow) {
    return null;
  }

  async function answer(follow: boolean) {
    const token = getAccessToken();
    if (!token || busy) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (follow) {
        await api.setSubscription(token, instanceId, { sectionKey: null, segmentKey: null, channel: "InApp", mode: "Subscribe" });
      }
      await api.answerFollowPrompt(token, instanceId);
      setAnswered(true);
      if (follow) {
        await onFollowed();
      }
    } catch {
      setError(follow ? "That didn't work — you can still subscribe with the bell at the top." : "That didn't work — try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="kd-card"
      role="region"
      aria-label="Notifications for this dial"
      style={{ padding: "var(--pad-card, 16px)", margin: "0 0 14px", borderLeft: "3px solid var(--accent)" }}
    >
      <div style={{ fontSize: 14.5, fontWeight: 700, color: "var(--text)" }}>
        Get notified about {dialName}?
      </div>
      <p style={{ margin: "6px 0 12px", fontSize: 13.5, lineHeight: 1.5, color: "var(--text-muted)" }}>
        {isGroup
          ? "Following this group tells you when a status changes on any dial in it. You can mute a single dial later from its bell."
          : `Following this dial tells you when a status changes on it. To follow just one section or ${noun.one} instead, use its bell.`}{" "}
        Messages don't come this way — people @mention you when they need you.
      </p>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button onClick={() => void answer(true)} disabled={busy} className="kd-btn-primary" style={{ fontSize: 13 }}>
          {isGroup ? "Follow this group" : "Follow this dial"}
        </button>
        {/* kd-btn-dashed is the full-width "add" button; sized to its label here, as a secondary answer. */}
        <button onClick={() => void answer(false)} disabled={busy} className="kd-btn-dashed" style={{ fontSize: 13, width: "auto", padding: "8px 16px" }}>
          Not now
        </button>
      </div>
      {error && (
        <div role="alert" style={{ marginTop: 8, fontSize: 12.5, color: "var(--st-risk, #c14257)" }}>
          {error}
        </div>
      )}
    </div>
  );
}
